import { useCallback, useRef, useState } from "react";

import {
  getPreferenceChannel,
  PREFERENCE_KEYS,
} from "~/v3-poc/kernel/channels/preferences";
import { usePreferenceSync } from "./usePreferenceSync";

export type AtdTheme = "light" | "dark";
export type AtdNavPosition = "top" | "left";
/** Mirrors `WidgetGroupLinePosition`; kept here so preferences own no UI imports. */
export type AtdWidgetGroupLinePosition = "top" | "bottom" | "off";

const THEME_STORAGE_KEY = PREFERENCE_KEYS.theme;
const NAV_POSITION_STORAGE_KEY = PREFERENCE_KEYS.navPosition;
/** Window-local; not in `PREFERENCE_KEYS`, so it never fans out. */
const NAV_COLLAPSED_STORAGE_KEY = "atd:preferences:nav:collapsed";
const WIDGET_GROUP_LINE_POSITION_STORAGE_KEY =
  PREFERENCE_KEYS.widgetGroupLinePosition;

const DEFAULT_THEME: AtdTheme = "dark";
const DEFAULT_NAV_POSITION: AtdNavPosition = "left";
const DEFAULT_NAV_COLLAPSED = false;
const DEFAULT_WIDGET_GROUP_LINE_POSITION: AtdWidgetGroupLinePosition = "top";

const readStoredValue = <Value>(
  key: string,
  defaultValue: Value,
  parse: (stored: string) => Value | undefined,
): Value => {
  try {
    const stored = window.localStorage.getItem(key);
    return stored === null ? defaultValue : (parse(stored) ?? defaultValue);
  } catch {
    return defaultValue;
  }
};

const writeStoredValue = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Persistence is best-effort; React state remains authoritative in-session.
  }
};

/** Persist for reload, then tell the other windows. */
const publishStoredValue = (key: string, value: string) => {
  writeStoredValue(key, value);
  getPreferenceChannel().post({ key, value });
};

const parseTheme = (stored: string): AtdTheme | undefined =>
  stored === "light" || stored === "dark" ? stored : undefined;

const parseNavPosition = (stored: string): AtdNavPosition | undefined =>
  stored === "top" || stored === "left" ? stored : undefined;

const parseNavCollapsed = (stored: string): boolean | undefined => {
  if (stored === "true") {
    return true;
  }
  if (stored === "false") {
    return false;
  }
  return undefined;
};

const parseWidgetGroupLinePosition = (
  stored: string,
): AtdWidgetGroupLinePosition | undefined =>
  stored === "top" || stored === "bottom" || stored === "off"
    ? stored
    : undefined;

export const useAtdPreferences = () => {
  const [theme, setThemeState] = useState<AtdTheme>(() =>
    readStoredValue(THEME_STORAGE_KEY, DEFAULT_THEME, parseTheme),
  );
  const themeRef = useRef(theme);
  const [navPosition, setNavPositionState] = useState<AtdNavPosition>(() =>
    readStoredValue(
      NAV_POSITION_STORAGE_KEY,
      DEFAULT_NAV_POSITION,
      parseNavPosition,
    ),
  );
  const [navCollapsed, setNavCollapsedState] = useState<boolean>(() =>
    readStoredValue(
      NAV_COLLAPSED_STORAGE_KEY,
      DEFAULT_NAV_COLLAPSED,
      parseNavCollapsed,
    ),
  );
  const [widgetGroupLinePosition, setWidgetGroupLinePositionState] =
    useState<AtdWidgetGroupLinePosition>(() =>
      readStoredValue(
        WIDGET_GROUP_LINE_POSITION_STORAGE_KEY,
        DEFAULT_WIDGET_GROUP_LINE_POSITION,
        parseWidgetGroupLinePosition,
      ),
    );

  const applyTheme = useCallback((nextTheme: AtdTheme) => {
    themeRef.current = nextTheme;
    setThemeState(nextTheme);
  }, []);

  usePreferenceSync(THEME_STORAGE_KEY, parseTheme, applyTheme);
  usePreferenceSync(
    NAV_POSITION_STORAGE_KEY,
    parseNavPosition,
    setNavPositionState,
  );
  usePreferenceSync(
    WIDGET_GROUP_LINE_POSITION_STORAGE_KEY,
    parseWidgetGroupLinePosition,
    setWidgetGroupLinePositionState,
  );

  const setTheme = useCallback((nextTheme: AtdTheme) => {
    themeRef.current = nextTheme;
    publishStoredValue(THEME_STORAGE_KEY, nextTheme);
    setThemeState(nextTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(themeRef.current === "dark" ? "light" : "dark");
  }, [setTheme]);

  const setNavPosition = useCallback((nextPosition: AtdNavPosition) => {
    publishStoredValue(NAV_POSITION_STORAGE_KEY, nextPosition);
    setNavPositionState(nextPosition);
  }, []);

  const setNavCollapsed = useCallback((nextCollapsed: boolean) => {
    writeStoredValue(NAV_COLLAPSED_STORAGE_KEY, String(nextCollapsed));
    setNavCollapsedState(nextCollapsed);
  }, []);

  const setWidgetGroupLinePosition = useCallback(
    (nextPosition: AtdWidgetGroupLinePosition) => {
      publishStoredValue(WIDGET_GROUP_LINE_POSITION_STORAGE_KEY, nextPosition);
      setWidgetGroupLinePositionState(nextPosition);
    },
    [],
  );

  return {
    theme,
    setTheme,
    toggleTheme,
    navPosition,
    setNavPosition,
    navCollapsed,
    setNavCollapsed,
    widgetGroupLinePosition,
    setWidgetGroupLinePosition,
  };
};
